import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  X,
  Clock,
  BookOpen,
  HelpCircle,
  ChevronDown,
  Eye,
  MessageSquare,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

interface Guide {
  id: string;
  slug: string;
  title: string;
}

interface GuideSubmission {
  id: string;
  user_id: string;
  submission_type: 'term' | 'faq';
  suggested_guide_id: string | null;
  suggested_category: string | null;
  content: {
    term?: string;
    explanation?: string;
    question?: string;
    answer?: string;
  };
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  guide?: Guide;
  user?: { first_name: string; last_name: string; email: string };
}

export function GuideSubmissionsPage() {
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState<string>('pending');
  const [filterType, setFilterType] = useState<string>('');
  const [selectedSubmission, setSelectedSubmission] = useState<GuideSubmission | null>(null);
  const [adminNotes, setAdminNotes] = useState('');

  // Fetch guides for display
  const { data: guides } = useQuery({
    queryKey: ['admin-guides'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guides')
        .select('id, slug, title')
        .order('order_index');
      if (error) throw error;
      return data as Guide[];
    },
  });

  // Fetch submissions
  const { data: submissions, isLoading } = useQuery({
    queryKey: ['admin-guide-submissions', filterStatus, filterType],
    queryFn: async () => {
      let query = supabase
        .from('guide_submissions')
        .select(`
          *,
          guide:guides(id, slug, title)
        `)
        .order('created_at', { ascending: false });

      if (filterStatus) {
        query = query.eq('status', filterStatus);
      }
      if (filterType) {
        query = query.eq('submission_type', filterType);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as GuideSubmission[];
    },
  });

  // Update submission status
  const updateStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      notes,
    }: {
      id: string;
      status: 'approved' | 'rejected';
      notes?: string;
    }) => {
      const { error } = await (supabase.from('guide_submissions') as any)
        .update({
          status,
          admin_notes: notes || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-submissions'] });
      setSelectedSubmission(null);
      setAdminNotes('');
      toast.success(
        variables.status === 'approved' ? 'Vorschlag angenommen' : 'Vorschlag abgelehnt'
      );
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const getGuideName = (guideId: string | null) => {
    if (!guideId) return 'Nicht zugeordnet';
    return guides?.find((g) => g.id === guideId)?.title || 'Unbekannt';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
            <Clock className="w-3 h-3" />
            Ausstehend
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            <Check className="w-3 h-3" />
            Angenommen
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            <X className="w-3 h-3" />
            Abgelehnt
          </span>
        );
      default:
        return null;
    }
  };

  // Stats
  const pendingCount = submissions?.filter((s) => s.status === 'pending').length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Guide-Vorschläge</h1>
            <p className="text-slate-500 mt-1">
              Nutzer-Einsendungen für neue Begriffe und Fragen
            </p>
          </div>
          {pendingCount > 0 && (
            <div className="bg-amber-100 text-amber-700 px-4 py-2 rounded-lg">
              <span className="font-bold">{pendingCount}</span> ausstehende Vorschläge
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-900">
              {submissions?.length || 0}
            </div>
            <div className="text-sm text-slate-500">Gesamt</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-amber-600">
              {submissions?.filter((s) => s.status === 'pending').length || 0}
            </div>
            <div className="text-sm text-slate-500">Ausstehend</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {submissions?.filter((s) => s.status === 'approved').length || 0}
            </div>
            <div className="text-sm text-slate-500">Angenommen</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">
              {submissions?.filter((s) => s.status === 'rejected').length || 0}
            </div>
            <div className="text-sm text-slate-500">Abgelehnt</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex gap-4">
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-4 pr-8 py-2 border border-slate-200 rounded-lg appearance-none
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="">Alle Status</option>
              <option value="pending">Ausstehend</option>
              <option value="approved">Angenommen</option>
              <option value="rejected">Abgelehnt</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-4 pr-8 py-2 border border-slate-200 rounded-lg appearance-none
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="">Alle Typen</option>
              <option value="term">Fachbegriffe</option>
              <option value="faq">FAQs</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Submissions List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : submissions?.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Keine Vorschläge vorhanden</div>
        ) : (
          submissions?.map((submission) => (
            <div key={submission.id} className="p-4 hover:bg-slate-50">
              <div className="flex items-start gap-4">
                {/* Type Icon */}
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    submission.submission_type === 'term'
                      ? 'bg-purple-100'
                      : 'bg-blue-100'
                  }`}
                >
                  {submission.submission_type === 'term' ? (
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  ) : (
                    <HelpCircle className="w-5 h-5 text-blue-600" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-slate-900">
                      {submission.submission_type === 'term'
                        ? submission.content.term
                        : submission.content.question}
                    </span>
                    {getStatusBadge(submission.status)}
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                    {submission.submission_type === 'term'
                      ? submission.content.explanation
                      : submission.content.answer}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="bg-slate-100 px-2 py-0.5 rounded">
                      {submission.submission_type === 'term' ? 'Begriff' : 'FAQ'}
                    </span>
                    <span>{getGuideName(submission.suggested_guide_id)}</span>
                    {submission.suggested_category && (
                      <span>Kategorie: {submission.suggested_category}</span>
                    )}
                    <span>
                      {new Date(submission.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setAdminNotes(submission.admin_notes || '');
                    }}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                    title="Details anzeigen"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {submission.status === 'pending' && (
                    <>
                      <button
                        onClick={() =>
                          updateStatus.mutate({
                            id: submission.id,
                            status: 'approved',
                          })
                        }
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title="Annehmen"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          updateStatus.mutate({
                            id: submission.id,
                            status: 'rejected',
                          })
                        }
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Ablehnen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSelectedSubmission(null)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Vorschlag Details</h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="p-2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedSubmission.status)}
                <span className="text-sm text-slate-400">
                  {new Date(selectedSubmission.created_at).toLocaleString('de-DE')}
                </span>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Typ
                </label>
                <p className="text-slate-900">
                  {selectedSubmission.submission_type === 'term'
                    ? 'Fachbegriff'
                    : 'FAQ'}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Guide
                </label>
                <p className="text-slate-900">
                  {getGuideName(selectedSubmission.suggested_guide_id)}
                </p>
              </div>

              {selectedSubmission.suggested_category && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Vorgeschlagene Kategorie
                  </label>
                  <p className="text-slate-900">{selectedSubmission.suggested_category}</p>
                </div>
              )}

              {selectedSubmission.submission_type === 'term' ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Begriff
                    </label>
                    <p className="text-slate-900 font-medium">
                      {selectedSubmission.content.term}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Erklärung
                    </label>
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {selectedSubmission.content.explanation}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Frage
                    </label>
                    <p className="text-slate-900 font-medium">
                      {selectedSubmission.content.question}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Antwort
                    </label>
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {selectedSubmission.content.answer}
                    </p>
                  </div>
                </>
              )}

              {selectedSubmission.status === 'pending' && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    <MessageSquare className="w-3 h-3 inline mr-1" />
                    Admin-Notiz (optional)
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    placeholder="Notiz für interne Zwecke..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none
                               focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  />
                </div>
              )}

              {selectedSubmission.admin_notes && selectedSubmission.status !== 'pending' && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Admin-Notiz
                  </label>
                  <p className="text-slate-600 text-sm bg-slate-50 p-2 rounded">
                    {selectedSubmission.admin_notes}
                  </p>
                </div>
              )}
            </div>

            {selectedSubmission.status === 'pending' && (
              <div className="flex gap-3 p-4 border-t">
                <button
                  onClick={() =>
                    updateStatus.mutate({
                      id: selectedSubmission.id,
                      status: 'rejected',
                      notes: adminNotes,
                    })
                  }
                  disabled={updateStatus.isPending}
                  className="flex-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Ablehnen
                </button>
                <button
                  onClick={() =>
                    updateStatus.mutate({
                      id: selectedSubmission.id,
                      status: 'approved',
                      notes: adminNotes,
                    })
                  }
                  disabled={updateStatus.isPending}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Annehmen
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
